import { Box, Button, Container, MenuItem, Select, Stack, Typography } from '@common/components'
import { SubSettingsContainer } from '@common/containers'
import { LanguagesAutocomplete } from '@features/languages/components'
import { useUser } from '@features/auth/hooks'
import { useModulesSubmitModuleResultMutation } from '@app/services/auto-generated/modules'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { updateLanguagesSchema } from '@common/utils/validations/auth.validations'
import { ModuleResult } from '@app/services/auto-generated/auth'
import useAccents from '@features/languages/hooks/use-accent'
import React, { useEffect } from 'react'

interface AccentForm {
  accent: string
}

const SubmitAccentForm: React.FC = (props: AccentForm) => {
  const { user } = useUser()
  const { enhanceAccents, languages, isSuccess: isSuccessLanguages } = useAccents()

  const [submitModule, { isLoading }] = useModulesSubmitModuleResultMutation()
  const {
    handleSubmit,
    control,
    formState: { isDirty },
    getValues,
    reset,
  } = useForm({
    defaultValues: {
      accent: enhanceAccents(user?.accent),
      voiceType: user?.voice_gender,
    },
    values: {
      accent: enhanceAccents(user?.accent),
      voiceType: user?.voice_gender,
    },
    resolver: yupResolver(updateLanguagesSchema),
  })

  const onSubmit = async (formState) => {
    try {
      const formData: ModuleResult = {
        id: 'PreferredAccentModule',
        data: {
          preferred_accent: formState.accent.code,
          preferred_voice_type: formState.voiceType,
        },
      }
      const result = await submitModule({ moduleResult: formData })

      result.unwrap()
    } catch (error) {
      return
    }
  }

  useEffect(() => {
    if (isSuccessLanguages) {
      reset({
        accent: enhanceAccents(user?.accent),
        voiceType: user?.voice_gender,
      })
    }
  }, [isSuccessLanguages])

  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)}>
      <Typography variant='body1' sx={{ mb: 2 }}>
        Choose an accent
      </Typography>
      <LanguagesAutocomplete
        name='accent'
        options={languages}
        defaultValue={getValues('accent')}
        control={control}
        isMultiple={false}
        placeholder={'The accent, I want the feedback in'}
      />
      <Typography variant='body1' sx={{ my: 2 }}>
        Choose feedback voice type
      </Typography>
      <Select name='voiceType' control={control}>
        <MenuItem value='MALE'>Male</MenuItem>
        <MenuItem value='FEMALE'>Female</MenuItem>
      </Select>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='flex-end'
        spacing={2}
        sx={{ mt: 3 }}
      >
        <Button disabled={!isDirty} variant='outlined' color='neutral' onClick={() => reset({})}>
          cancel
        </Button>
        <Button type='submit' disabled={!isDirty} variant='contained' loading={isLoading}>
          Save Profile
        </Button>
      </Stack>
    </Box>
  )
}

const PreferredAccent = () => {
  return (
    <SubSettingsContainer title='Accent settings'>
      <Container maxWidth='sm'>
        <SubmitAccentForm />
      </Container>
    </SubSettingsContainer>
  )
}

export default PreferredAccent
